import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  AUDIENCES,
  audienceLabel,
  deleteFaq,
  getFaqs,
  saveFaq,
} from "../../../store/actions/councilActions/faqAction";
import { getTrades } from "../../../store/actions/councilActions/trade";
import { errorAlert } from "../../../utils/alert";

/*
  FAQs, council side.

  Every question belongs to one section of the website, and may in addition
  belong to a main trade - a question about Skill Development courses is only
  useful next to Skill Development. Sub trades are deliberately not offered:
  the user asked for main categories only.

  Order is by the number in "Order", then by age, so the council can put the
  common questions at the top without renumbering everything.
*/
const EMPTY = {
  audience: "student",
  trade_id: "",
  question: "",
  answer: "",
  sort_order: 0,
  is_active: 1,
};

const FaqList = () => {
  const dispatch = useDispatch();

  const [list, setList] = useState(null);
  const [counts, setCounts] = useState({});
  const [audience, setAudience] = useState("student");
  const [trades, setTrades] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setList(null);
    const { list: rows, counts: totals } = await dispatch(getFaqs({ audience }));
    setList(rows);
    setCounts(totals);
  }, [dispatch, audience]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    dispatch(getTrades()).then((rows) => setTrades(rows || []));
  }, [dispatch]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.question.trim()) return errorAlert("Please write the question.");
    if (!form.answer.trim()) return errorAlert("Please write the answer.");

    setBusy(true);
    const body = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key !== "id") body.append(key, value === null ? "" : value);
    });

    const done = await dispatch(saveFaq(body, form.id));
    setBusy(false);

    if (done) {
      // A question moved to another section should not vanish silently, so
      // the list follows it.
      setAudience(form.audience);
      setForm(null);
      load();
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.question}"?`)) return;
    if (await dispatch(deleteFaq(row.id))) load();
  };

  return (
    <div>
      <div class="d-flex justify-content-between align-items-center">
        <div class="BigTitle">FAQs</div>
        <button
          type="button"
          class="btn btn-comman"
          onClick={() => setForm({ ...EMPTY, audience })}
        >
          Add FAQ
        </button>
      </div>

      {/* one tab per section of the website */}
      <ul class="nav nav-tabs mt-3">
        {AUDIENCES.map((a) => (
          <li class="nav-item" key={a.key}>
            <button
              type="button"
              class={`nav-link ${audience === a.key ? "active" : ""}`}
              onClick={() => setAudience(a.key)}
            >
              {a.label} ({counts[a.key] ?? 0})
            </button>
          </li>
        ))}
      </ul>

      <div class="table-responsive mt-3">
        <table class="table">
          <thead>
            <tr>
              <th style={{ width: "70px" }}>Order</th>
              <th>Question</th>
              <th>Trade</th>
              <th>Status</th>
              <th style={{ width: "140px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {list === null ? (
              <tr>
                <td colSpan={5}>Loading...</td>
              </tr>
            ) : list.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  No FAQ added for {audienceLabel(audience)} yet.
                </td>
              </tr>
            ) : (
              list.map((row) => (
                <tr key={row.id}>
                  <td>{row.sort_order}</td>
                  <td>{row.question}</td>
                  <td>{row.trade?.name || "--"}</td>
                  <td>
                    {Number(row.is_active) === 1 ? (
                      <span class="text-success">Live</span>
                    ) : (
                      <span class="text-muted">Hidden</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      class="btn btn-sm btn-light-primary mr-1"
                      onClick={() =>
                        setForm({
                          id: row.id,
                          audience: row.audience || "general",
                          trade_id: row.trade_id || "",
                          question: row.question || "",
                          answer: row.answer || "",
                          sort_order: Number(row.sort_order) || 0,
                          is_active: Number(row.is_active),
                        })
                      }
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-danger"
                      onClick={() => remove(row)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal show={!!form} onHide={() => setForm(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{form?.id ? "Edit FAQ" : "Add FAQ"}</Modal.Title>
        </Modal.Header>
        {form && (
          <form onSubmit={submit}>
            <Modal.Body>
              <div class="row">
                <div class="col-md-4 mb-2">
                  <label>Show in *</label>
                  <select
                    class="form-control"
                    value={form.audience}
                    onChange={(e) =>
                      setForm({ ...form, audience: e.target.value })
                    }
                  >
                    {AUDIENCES.map((a) => (
                      <option value={a.key} key={a.key}>
                        {a.label} section
                      </option>
                    ))}
                  </select>
                </div>
                <div class="col-md-4 mb-2">
                  <label>Trade</label>
                  {/* main trades only - the user asked for main categories */}
                  <select
                    class="form-control"
                    value={form.trade_id}
                    onChange={(e) =>
                      setForm({ ...form, trade_id: e.target.value })
                    }
                  >
                    <option value="">-- not about one trade --</option>
                    {trades.map((t) => (
                      <option value={t.id} key={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div class="col-md-2 mb-2">
                  <label>Order</label>
                  <input
                    type="number"
                    class="form-control"
                    value={form.sort_order}
                    onChange={(e) =>
                      setForm({ ...form, sort_order: e.target.value })
                    }
                  />
                </div>
                <div class="col-md-2 mb-2">
                  <label>Status</label>
                  <select
                    class="form-control"
                    value={form.is_active}
                    onChange={(e) =>
                      setForm({ ...form, is_active: Number(e.target.value) })
                    }
                  >
                    <option value={1}>Live</option>
                    <option value={0}>Hidden</option>
                  </select>
                </div>

                <div class="col-md-12 mb-2">
                  <label>Question *</label>
                  <input
                    type="text"
                    class="form-control"
                    value={form.question}
                    onChange={(e) =>
                      setForm({ ...form, question: e.target.value })
                    }
                    required
                  />
                </div>
                <div class="col-md-12 mb-2">
                  <label>Answer *</label>
                  <textarea
                    class="form-control"
                    rows={5}
                    value={form.answer}
                    onChange={(e) =>
                      setForm({ ...form, answer: e.target.value })
                    }
                    required
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                type="button"
                class="btn btn-secondary"
                onClick={() => setForm(null)}
              >
                Cancel
              </button>
              <button type="submit" class="btn btn-comman" disabled={busy}>
                {busy ? "Saving..." : "Save"}
              </button>
            </Modal.Footer>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default FaqList;
